import { useState } from "react";
import QuickViewModal from "./QuickViewModal";
import { useCart } from "../../context/CartContext";
import "../../styles/style_boutique.css";

// Icônes de taille avec émojis floraux
const SIZE_ICONS = {
  M: { icon: "🌱", label: "M", description: "Moyen" },
  L: { icon: "🌿", label: "L", description: "Grand" },
  XL: { icon: "🌳", label: "XL", description: "Très Grand" }
};

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const [selectedVariant, setSelectedVariant] = useState(
    product.variantes?.[0] || null
  );
  const [selectedBouquetType, setSelectedBouquetType] = useState(product.bouquetTypeDefault || null);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [showQuickView, setShowQuickView] = useState(false);

  const hasMultipleVariants = product.variantes && product.variantes.length > 1;
  
  // Calculer le prix TTC
  const calculateTTC = (prixHT, tva) => {
    return (prixHT * (1 + tva / 100)).toFixed(2);
  };

  const currentPrice = selectedVariant 
    ? calculateTTC(selectedVariant.prixHT, product.tva)
    : "0.00";

  const handleAddToCart = () => {
    if (selectedVariant) {
      addToCart(product, selectedVariant, quantity, selectedBouquetType);
      // Reset quantity après ajout
      setQuantity(1);
    }
  };

  // Image placeholder avec gradient floral
  const placeholderStyle = {
    background: `linear-gradient(135deg, 
      hsl(${(product.id * 30) % 360}, 70%, 85%), 
      hsl(${(product.id * 30 + 40) % 360}, 60%, 75%))`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '4rem'
  };

  const flowerEmojis = ["🌸", "🌺", "🌷", "🌹", "💐", "🌻", "🪻", "🪷"];
  const randomFlower = flowerEmojis[product.id % flowerEmojis.length];

  return (
    <div 
      className={`product-card ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Badge stock faible */}
      {selectedVariant && selectedVariant.stock <= 3 && selectedVariant.stock > 0 && (
        <div className="stock-badge low">
          <ion-icon name="alert-circle-outline"></ion-icon>
          Plus que {selectedVariant.stock}
        </div>
      )}
      
      {/* Badge rupture */}
      {selectedVariant && selectedVariant.stock === 0 && (
        <div className="stock-badge out">
          <ion-icon name="close-circle-outline"></ion-icon>
          Rupture
        </div>
      )}

      {/* Image du produit */}
      <div className="product-image-container">
        {product.imgPrincipal ? (
          <img 
            src={product.imgPrincipal}
            alt={product.libelle}
            className={`product-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
          />
        ) : (
          <div className="product-image-placeholder" style={placeholderStyle}>
            {randomFlower}
          </div>
        )}
        
        {/* Overlay au hover */}
        <div className="product-overlay">
          <button className="quick-view-btn" onClick={() => setShowQuickView(true)}>
            <ion-icon name="eye-outline"></ion-icon>
            Aperçu rapide
          </button>
        </div>

        {/* Catégorie */}
        <span className="product-category-tag">{product.categorie}</span>
      </div>

      {/* Contenu du produit */}
      <div className="product-content">
        <h3 className="product-title">{product.libelle}</h3>
        <p className="product-description">{product.description}</p>

        {/* Sélecteur de taille si plusieurs variantes */}
        {hasMultipleVariants && (
          <div className="size-selector">
            <span className="size-label">Choisir la taille :</span>
            <div className="size-options">
              {product.variantes.map((variant) => {
                const sizeInfo = SIZE_ICONS[variant.taille_code] || { 
                  icon: "📦", 
                  label: variant.taille_code,
                  description: variant.taille_libelle 
                };
                const isSelected = selectedVariant?.taille_code === variant.taille_code;
                const isOutOfStock = variant.stock === 0;

                return (
                  <button
                    key={variant.taille_code}
                    className={`size-btn ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'disabled' : ''}`}
                    onClick={() => !isOutOfStock && setSelectedVariant(variant)}
                    disabled={isOutOfStock}
                    title={`${sizeInfo.description} - ${calculateTTC(variant.prixHT, product.tva)}€`}
                  >
                    <span className="size-icon">{sizeInfo.icon}</span>
                    <span className="size-code">{sizeInfo.label}</span>
                    {isOutOfStock && <span className="size-out">✕</span>}
                  </button>
                );
              })}
            </div>
            {selectedVariant && (
              <span className="selected-size-info">
                {selectedVariant.taille_libelle}
              </span>
            )}
          </div>
        )}

        {/* Sélecteur tige/bulle si disponible */}
        {Array.isArray(product.bouquetTypeOptions) && product.bouquetTypeOptions.length > 0 && (
          <div className="bouquet-type-selector">
            <span className="bouquet-type-label">Type de bouquet :</span>
            <div className="bouquet-type-options">
              {product.bouquetTypeOptions.map((type) => (
                <button
                  key={type}
                  className={`bouquet-type-btn ${selectedBouquetType === type ? 'selected' : ''}`}
                  onClick={() => setSelectedBouquetType(type)}
                  type="button"
                >
                  {type === "tige" ? "Tige" : "Bulle"}
                </button>
              ))}
            </div>
            <span className="bouquet-type-info">{selectedBouquetType ? `Sélectionné : ${selectedBouquetType === "tige" ? "Tige" : "Bulle"}` : "(optionnel)"}</span>
          </div>
        )}

        {/* Prix et actions */}
        <div className="product-footer">
          <div className="price-container">
            <span className="price-label">Prix TTC</span>
            <span className="price">{currentPrice} €</span>
          </div>

          <div className="product-actions">
            {/* Sélecteur de quantité */}
            <div className="quantity-selector">
              <button 
                className="qty-btn minus"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <ion-icon name="remove-outline"></ion-icon>
              </button>
              <span className="qty-value">{quantity}</span>
              <button 
                className="qty-btn plus"
                onClick={() => setQuantity(Math.min(selectedVariant?.stock || 10, quantity + 1))}
                disabled={quantity >= (selectedVariant?.stock || 10)}
              >
                <ion-icon name="add-outline"></ion-icon>
              </button>
            </div>

            {/* Bouton ajouter au panier */}
            <button 
              className="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={!selectedVariant || selectedVariant.stock === 0}
            >
              <ion-icon name="cart-outline"></ion-icon>
              <span>Ajouter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Effet de brillance */}
      <div className="card-shine"></div>

      {/* Modal aperçu rapide */}
      <QuickViewModal
        product={product}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
      />
    </div>
  );
}
